import { BookCopy, Ellipsis, LoaderCircle, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import TablePageControls from 'components/storefront/components/TablePageControls';
import TableSortDropdown from 'components/storefront/components/TableSortDropdown';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { Skeleton } from 'shadcn/components/ui/Skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';
/**
 * Data table for displaying and managing storefront configurations
 *
 * @param {Object} props
 * @param {Array} props.configs - Array of configuration objects
 * @param {number} props.currentPage - Current page number
 * @param {Array} props.currentRows - Array of rows for current page
 * @param {Function} props.deleteConfigs - Function to delete selected configurations
 * @param {Function} props.handleSelectRow - Function to handle row selection
 * @param {Function} props.onDuplicateConfig - Function to open the duplicate config dialog
 * @param {Function} props.onPageChange - Function to handle page changes
 * @param {Function} props.onRowsPerPageChange - Function to handle rows per page changes
 * @param {Function} props.onSearchQueryChange - Function to handle search query changes
 * @param {Function} props.onSelectAll - Function to handle select all rows
 * @param {Function} props.onSort - Function to handle sorting
 * @param {string} props.rowsPerPage - Number of rows per page
 * @param {string} props.searchQuery - Current search query
 * @param {Set} props.selectedRows - Set of selected row IDs
 * @param {Object} props.sortState - Current sort state {key, direction}
 * @param {number} props.totalPages - Total number of pages
 * @returns {JSX.Element} ConfigTable component
 */
function ConfigTable({
    configs,
    currentPage,
    currentRows,
    deleteConfigs,
    handleSelectRow,
    onDuplicateConfig,
    onPageChange,
    onRowsPerPageChange,
    onSearchQueryChange,
    onSelectAll,
    onSort,
    rowsPerPage,
    searchQuery,
    selectedRows,
    sortState,
    totalPages,
}) {
    return (
        <div className="flex flex-col gap-4 w-full">
            <Input
                type="text"
                placeholder="Search configurations..."
                value={searchQuery}
                onChange={onSearchQueryChange}
                className="bg-background"
            />
            <Table className="bg-background rounded-md border">
                <TableHeader>
                    <TableRow className="[&_th]:text-muted-foreground [&_th]:text-xs [&_th]:font-normal hover:bg-muted [&_th]:bg-muted">
                        <TableHead className="min-w-15 w-content">
                            <Checkbox
                                checked={currentRows.length > 0 && selectedRows.size === currentRows.length}
                                onCheckedChange={onSelectAll}
                                className="bg-background"
                            />
                        </TableHead>
                        <TableHead className="min-w-25">
                            <TableSortDropdown
                                active={sortState.key === 'id'}
                                direction={sortState.direction}
                                label="ID"
                                onSort={onSort}
                                sortKey="id"
                            />
                        </TableHead>
                        <TableHead className="w-[100%]">
                            <TableSortDropdown
                                active={sortState.key === 'name'}
                                direction={sortState.direction}
                                label="Name"
                                onSort={onSort}
                                sortKey="name"
                            />
                        </TableHead>
                        <TableHead>
                            <TableSortDropdown
                                active={sortState.key === 'segment'}
                                direction={sortState.direction}
                                label="Segment"
                                onSort={onSort}
                                sortKey="segment"
                            />
                        </TableHead>
                        <TableHead>
                            <TableSortDropdown
                                active={sortState.key === 'published'}
                                direction={sortState.direction}
                                label="Published"
                                onSort={onSort}
                                sortKey="published"
                            />
                        </TableHead>
                        <TableHead>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        className="data-[state=open]:bg-primary/5 hover:bg-primary/5 hover:[&_svg]:text-accent-foreground"
                                        variant="ghost"
                                        size="iconSm"
                                        type="button"
                                    >
                                        <Ellipsis className="text-muted-foreground" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        variant="destructive"
                                        onClick={() => deleteConfigs([...selectedRows])}
                                        disabled={selectedRows?.size === 0}
                                    >
                                        <Trash2 className="h-4 w-4 stroke-1" /> Delete selected
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        variant="destructive"
                                        onClick={() => deleteConfigs(configs.map((config) => config.id))}
                                        disabled={configs?.length === 0}
                                    >
                                        <Trash2 className="h-4 w-4 stroke-1" /> Delete all
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!configs && <RowsLoadingSkeleton />}

                    {configs && currentRows?.length === 0 && (
                        <TableRow>
                            <TableCell colSpan="6" className="py-4 text-center text-muted-foreground">
                                No configurations found
                            </TableCell>
                        </TableRow>
                    )}

                    {currentRows?.length > 0 &&
                        currentRows.map((config) => (
                            <TableRow key={config.id} className="h-12 text-left">
                                <TableCell>
                                    <Checkbox
                                        checked={selectedRows.has(config.id)}
                                        onCheckedChange={() => handleSelectRow(config.id)}
                                    />
                                </TableCell>
                                <TableCell>{config.id}</TableCell>
                                <TableCell>
                                    <Link
                                        to={`/production/storefront-configurations/${config.id}`}
                                        className="text-primary! hover:underline"
                                    >
                                        {config.name}
                                    </Link>
                                </TableCell>
                                <TableCell className="text-muted-foreground">{config.segment || 'None'}</TableCell>
                                <TableCell>
                                    <div className="h-full flex items-center justify-center">
                                        {config.published ? (
                                            <div className="size-1.5 -ml-3 rounded-full bg-green-500" />
                                        ) : (
                                            <div className="size-1.5 -ml-3 rounded-full bg-yellow-500" />
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                className="data-[state=open]:bg-primary/5 hover:bg-primary/5 hover:[&_svg]:text-accent-foreground"
                                                variant="ghost"
                                                size="iconSm"
                                                type="button"
                                            >
                                                <Ellipsis className="text-muted-foreground" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => onDuplicateConfig(config)}>
                                                <BookCopy />
                                                Duplicate
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                variant="destructive"
                                                onClick={() => deleteConfigs([config.id])}
                                            >
                                                <Trash2 className="h-4 w-4 stroke-1" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            <TablePageControls
                currentPage={currentPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                selectedRows={selectedRows.size}
                totalRows={configs?.length ?? 0}
            />
        </div>
    );
}

/**
 * Loading skeleton for config rows
 *
 * @returns {JSX.Element} RowsLoadingSkeleton component
 */
function RowsLoadingSkeleton() {
    return Array.from({ length: 3 }).map((_, index) => (
        <TableRow key={index}>
            <TableCell>
                <Skeleton className="w-full h-5" />
            </TableCell>
            <TableCell>
                <Skeleton className="w-3/4 h-5" />
            </TableCell>
            <TableCell>
                <Skeleton
                    className={cn('h-5', index === 0 && 'w-1/2', index === 1 && 'w-3/4', index === 2 && 'w-1/3')}
                />
            </TableCell>
            <TableCell>
                <Skeleton className="h-5 w-3/4" />
            </TableCell>
            <TableCell>
                <Skeleton className="h-5 w-3/4" />
            </TableCell>
            <TableCell>
                <Skeleton className="h-5 w-3/4" />
            </TableCell>
        </TableRow>
    ));
}

export default ConfigTable;
